import classNames from 'classnames'
import { useCallback, useEffect, useState } from 'react'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { useScrollBlock } from 'src/utils/useScrollBlock'
import { useMobile } from 'src/hooks/useMobile'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { openShoppingMuse } from 'src/sdk/muse/openShoppingMuse'

import DiscoveryHubShelf from './DiscoveryHubShelf/DiscoveryHubShelf'
import Portal from './Portal'
import './ModalSearch.scss'
import ModalSearchTerms from './ModalSearchTerms'
import ModalSearchMobileTabs from './ModalSearchMobileTabs'
import UnmissableSection from './UnmissableSection'
import { MuseSearchCTAButton } from './MuseSearchCTAButton'
import type { Product } from '../../restructure/product/types/product'

type Props = {
  onClose: () => void
  onSearch: (searchTerm?: string) => void
  isOpen: boolean
  suggestions: Array<{
    term: string
  }>
  products: Product[]
  dynamicProductsSuggestions: Product[]
  dynamicRecommendations: Product[]
  term?: string
  resultNotFound: boolean
  userSearchedTerms: Array<{ term: string }>
  setUserSearchedTerms: (terms: Array<{ term: string }>) => void
  modalSearchEl: HTMLDivElement | null
}

export function ModalSearch({
  isOpen,
  onClose,
  onSearch,
  products,
  dynamicProductsSuggestions,
  suggestions,
  term,
  resultNotFound,
  userSearchedTerms,
  setUserSearchedTerms,
  modalSearchEl,
  dynamicRecommendations,
}: Props) {
  const { isMobile } = useMobile()
  const { pageData } = useGlobalContext()
  const standardObj = getStandardObject(pageData)
  const { allowScroll, blockScroll } = useScrollBlock()
  const [termsToShowAUser, setTermsToShowAUser] = useState<
    Array<{ term: string }>
  >([])

  useEffect(() => {
    const closeModal = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keyup', closeModal)

    return () => {
      window.removeEventListener('keyup', closeModal)
    }
  }, [onClose])

  useEffect(() => {
    isOpen ? blockScroll() : allowScroll()
  }, [allowScroll, blockScroll, isOpen])

  useEffect(() => {
    setTermsToShowAUser(
      term === '' && userSearchedTerms?.length > 0
        ? userSearchedTerms
        : suggestions
    )
  }, [suggestions, term, userSearchedTerms])

  const handleMuseCTAClick = useCallback(() => {
    openShoppingMuse()
  }, [])

  if (!isOpen) {
    return null
  }

  const errorToTermNotFound =
    (term !== '' && products.length === undefined) ||
    (term !== '' && resultNotFound)

  return (
    <div
      data-testid="discovery-hub-modal"
      className={classNames(
        'modal-discovery-hub pl-4 restructure-medium-desktop:pl-0 z-10 bg-restructure-background-primary restructure-small-desktop:min-w-[600px] shadow-1 w-screen restructure-small-desktop:h-[95vh] scrollbar-v2',
        // `fixed` resolves against `.header-container`, whose `show-header` transform makes it the containing block.
        'restructure-small-desktop:fixed restructure-small-desktop:top-[114px] restructure-small-desktop:left-0 restructure-medium-desktop:flex restructure-medium-desktop:justify-center'
      )}
    >
      <div
        className={`container-modal-discovery-hub flex flex-col w-full restructure-medium-desktop:scrollbar-v2  restructure-medium-desktop:flex-row restructure-medium-desktop:${
          term === '' ? 'justify-center' : 'justify-left'
        }`}
      >
        {term === '' && (
          <MuseSearchCTAButton onClick={handleMuseCTAClick} variant="mobile" />
        )}
        <div
          className={`term-and-shelf-conteiner ${
            term === ''
              ? 'restructure-medium-desktop:w-[60%] restructure-medium-desktop:pl-4 restructure-medium-desktop:pr-[87px]'
              : 'restructure-medium-desktop:w-full'
          }`}
        >
          <div
            className={`${
              term === ''
                ? 'restructure-medium-desktop:max-w-[692px] restructure-medium-desktop:ml-auto'
                : 'restructure-medium-desktop:max-w-[1410px] restructure-medium-desktop:mx-auto'
            } `}
          >
            {!term && isMobile && (
              <ModalSearchMobileTabs modalSearchEl={modalSearchEl} />
            )}
            {term === '' && (
              <MuseSearchCTAButton
                onClick={handleMuseCTAClick}
                variant="desktop"
              />
            )}
            {term === '' && !!dynamicProductsSuggestions?.length && (
              <div className="mt-[22px]">
                <DiscoveryHubShelf
                  title="sugestões de busca"
                  testId="discovery-hub-shelf-dy"
                  setCloseSearchHubModal={() => onClose()}
                  products={dynamicProductsSuggestions}
                  dynamicRecommendations={dynamicRecommendations}
                />
              </div>
            )}
            {errorToTermNotFound && (
              <div>
                <p className="text-xl restructure-small-desktop:desktop-heading-title4 text-restructure-primary  border-b border-[#8a8a8a] py-6 mb-6 restructure-small-desktop:py-10 restructure-small-desktop:mb-10 mr-4">
                  Não encontramos resultados para ”{term}”.
                  <br /> Verifique a ortografia ou tente termos de pesquisa
                  menos específicos.
                </p>
                <DiscoveryHubShelf
                  title="sugestões de busca"
                  testId="discovery-hub-shelf-dy"
                  setCloseSearchHubModal={() => onClose()}
                  products={dynamicProductsSuggestions}
                  dynamicRecommendations={dynamicRecommendations}
                />
              </div>
            )}

            {termsToShowAUser && products.length !== undefined && (
              <ModalSearchTerms
                onSearch={onSearch}
                resultNotFound={resultNotFound}
                setUserSearchedTerms={setUserSearchedTerms}
                standardObj={standardObj}
                term={term}
                termsToShowAUser={termsToShowAUser}
                userSearchedTerms={userSearchedTerms}
              />
            )}
            {products.length > 0 && (
              <div
                className={`${
                  term === '' ? '' : 'restructure-small-desktop:pl-5'
                }`}
              >
                <h3 className="font-normal font-inter text-base restructure-small-desktop:text-font-size-5 mb-4 mt-8">
                  Nossas sugestões
                </h3>
                <DiscoveryHubShelf
                  title="Recomendações"
                  testId="discovery-hub-shelf-recommendations"
                  setCloseSearchHubModal={() => onClose()}
                  products={products}
                />
              </div>
            )}
          </div>
        </div>
        {!term && (
          <UnmissableSection onClose={onClose} standardObj={standardObj} />
        )}
        {isOpen && (
          <Portal>
            <span
              onClick={() => onClose()}
              role="button"
              aria-hidden
              className="z-[999] hidden restructure-small-desktop:fixed restructure-small-desktop:block w-screen h-full top-[154px] left-0 bg-restructure-background-primary-inverted opacity-80"
            />
          </Portal>
        )}
      </div>
    </div>
  )
}
